import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
} from "react";
import { useSelector, shallowEqual, useDispatch } from "react-redux";
import {
  SG_ADD_TASK_TEMPLATE,
  SG_CREATE_TASK_SCHEDULE,
  CLEAR_TASK_TEMPLATE,
  SG_FETCH_TASK_TEMPLATE,
  SG_UPDATE_TASK_TEMPLATE,
  SG_ADD_EMAIL_TEMPLATE,
  SG_ADD_USER_TASK,
} from "constants/actions";
import { useNavigate } from "react-router";
import { useToasts } from "react-toast-notifications";
import { v4 as uuidv4 } from "uuid";
import { useUserSearchContext } from "Providers/UserSearchProvider";

// Create the context
const SurveyContext = createContext();

// Custom hook to use the context
export const useSurveyContext = () => useContext(SurveyContext);

// Provider component
export const SurveyProvider = ({
  children,
  initialFormData,
  surveyId,
  mode,
}) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { addToast } = useToasts();
  const { userList } = useUserSearchContext();

  // State management
  const [formData, setFormData] = useState({
    name: "",
    description: "",
    organizationName: "",
    organizationNameFr: "",
    feedbackTipsEnabled: false,
    questions:
      mode === "edit"
        ? []
        : [
            {
              id: uuidv4(),
              question: "",
              questionFrench: "",
              aiEnhanced: false,
              task_type: "question",
              type: { value: "text", label: "Open Text" },
            },
          ],
    assignedUsersList: [],
    collaborators: [],
    user_tasks: [
      {
        task_type: "survey_is_anonymous",
        value: true,
      },
    ],
    emailSubject: "",
    emailBody: "",
    emailBodyFr: "",
    selectedSport: initialFormData?.selectedSport || null,
    ...initialFormData, // Merge initialFormData
  });

  const [loading, setLoading] = useState(false);
  const [loadingMessage, setLoadingMessage] = useState("");
  const [error, setError] = useState(null);
  // Add state to track if the survey being edited is a draft
  const [isDraft, setIsDraft] = useState(false);

  // Validation function
  const validateFormData = (ignoreFields = []) => {
    const validationErrors = {};

    if (!ignoreFields.includes("name") && !formData.name.trim()) {
      validationErrors.surveyNameValidation = "Survey name cannot be empty";
    }

    // Organization name validation (required for submission)
    if (!ignoreFields.includes("organizationName") && !formData.organizationName?.trim()) {
      validationErrors.organizationNameValidation = "Organization name (English) is required";
    }

    if (!ignoreFields.includes("organizationNameFr") && !formData.organizationNameFr?.trim()) {
      validationErrors.organizationNameFrValidation = "Organization name (French) is required";
    }

    if (
      !ignoreFields.includes("questions") &&
      formData.questions.some((q) => !q.question.trim())
    ) {
      validationErrors.surveyQuestionValidation = "Question cannot be empty";
    }

    if (
      !ignoreFields.includes("emailSubject") &&
      !formData.emailSubject.trim()
    ) {
      validationErrors.emailSubjectValidation = "Email subject cannot be empty";
    }

    if (!ignoreFields.includes("emailBody") && !formData.emailBody.trim()) {
      validationErrors.emailBodyValidation = "Email body cannot be empty";
    }

    if (
      !ignoreFields.includes("emailBodyFr") &&
      !formData.emailBodyFr?.trim()
    ) {
      validationErrors.emailBodyFrValidation = "French email body is required";
    }

    if (Object.keys(validationErrors).length > 0) {
      const messages = Object.values(validationErrors);
      const msg = messages[0] || "Unable to save survey. Please check the form for errors";
      addToast(msg, { appearance: "error" });
    }

    return validationErrors;
  };

  // Redux selectors
  const {
    get_auth,
    createdTemplate,
    createdScheduledTemplate,
    fetchedTemplate,
    updatedTemplate,
    createdEmailTemplate,
    assignedUserTasks,
    draftSend,
    scheduledPulseSurveys,
  } = useSelector(
    (state) => ({
      get_auth: state.auth,
      createdTemplate: state.task_templates?.created,
      createdScheduledTemplate: state.task_templates?.created_scheduled_task,
      fetchedTemplate: state.task_templates?.fetched_template,
      updatedTemplate: state.task_templates?.updated_template,
      createdEmailTemplate: state.email_templates?.created_email_template,
      updatedEmailTemplate: state.email_templates?.updated_email_template,
      assignedUserTasks: state.task_templates?.assigned_user_tasks,
      draftSend: state.task_templates?.draftSend,
      scheduledPulseSurveys: state.task_templates.scheduled_tasks,
    }),
    shallowEqual
  );

  // Fetch survey data when in edit mode
  useEffect(() => {
    if (mode === "edit" && surveyId) {
      dispatch({ type: SG_FETCH_TASK_TEMPLATE, payload: surveyId });
    }
  }, [mode, surveyId, dispatch]);

  // Update form data when fetchedTemplate changes
  useEffect(() => {
    if (fetchedTemplate && fetchedTemplate.id) {
      // Check if this template has been scheduled (if so, it's not a draft)
      const isScheduled = scheduledPulseSurveys?.results?.some(
        (scheduledTask) => scheduledTask?.task_template_user_tasks === fetchedTemplate.id
      );
      
      // Set draft status - it's a draft if it hasn't been scheduled
      setIsDraft(!isScheduled);

      const assigneesList =
        fetchedTemplate.user_tasks.find(
          (task) => task.task_type === "assignee_draft"
        )?.assignees || [];

      const collaboratorsTask = fetchedTemplate.user_tasks.find(
        (task) => task.task_type === "collaborators"
      );

      const collaboratorsList = collaboratorsTask?.collaborators || [];

      // Convert collaborator IDs to user objects
      const collaboratorUsers = collaboratorsList.map(id => {
        const user = userList?.find(u => u.value === id);
        return user ? {
          key: user.value,
          value: user.value,
          text: user.text,
          id: user.value
        } : null;
      }).filter(Boolean);

      const descriptionTask = fetchedTemplate.user_tasks.find(
        (task) => task.task_type === "description"
      );
      const organizationNameEnTask = fetchedTemplate.user_tasks.find(
        (task) => task.task_type === "organization_name_en"
      );
      const organizationNameFrTask = fetchedTemplate.user_tasks.find(
        (task) => task.task_type === "organization_name_fr"
      );

      const organizationNameValue =
        fetchedTemplate.organizationName ||
        organizationNameEnTask?.text ||
        "";

      const organizationNameFrValue =
        fetchedTemplate.organizationNameFr ||
        organizationNameFrTask?.text ||
        "";

      const initialFormData = {
        name: fetchedTemplate.name || "",
        draftData: {
          name: fetchedTemplate.name || "",
          description: descriptionTask?.text || "",
          organizationName: organizationNameValue,
          organizationNameFr: organizationNameFrValue,
          feedbackTipsEnabled: fetchedTemplate.feedbackTipsEnabled || false,
          emailBody: fetchedTemplate.email_content || "",
          emailSubject: fetchedTemplate.email_subject || "",
          emailBodyFr: fetchedTemplate.email_content_fr || "",
          survey_is_anonymous: fetchedTemplate.user_tasks.find(
            (task) => task.task_type === "survey_is_anonymous"
          )?.value,
        },
        description: descriptionTask?.text || "",
        organizationName: organizationNameValue,
        organizationNameFr: organizationNameFrValue,
        feedbackTipsEnabled: fetchedTemplate.feedbackTipsEnabled || false,
        questions: fetchedTemplate?.user_tasks
          ?.filter((task) => task.task_type === "question")
          ?.map((task) => ({
            ...task,
            id: task.id,
            question: task.question,
            questionFrench: task.questionFrench || "",
            aiEnhanced: task.aiEnhanced || false,
            task_type: "question",
          })),
        assignedUsersList: [],
        assignedUsersDraft: assigneesList.length > 0 ? [...assigneesList] : [],
        collaborators: collaboratorUsers,
        emailSubject:
          fetchedTemplate.user_tasks.find(
            (task) => task.task_type === "email_draft"
          )?.subject || "",
        emailBody:
          fetchedTemplate.user_tasks.find(
            (task) => task.task_type === "email_draft"
          )?.body || "",
        emailBodyFr:
          fetchedTemplate.user_tasks.find(
            (task) => task.task_type === "email_draft"
          )?.body_fr || "",
        user_tasks: fetchedTemplate.user_tasks || [],
        selectedSport: fetchedTemplate?.selectedSport || null,
      };

      setFormData(initialFormData);
    }
  }, [fetchedTemplate]);

  // Update form data function
  const updateFormData = useCallback((updatedSection) => {
    setFormData((prev) => ({ ...prev, ...updatedSection }));
  }, []);

  const mapSportSelection = (sport) => {
    if (!sport) return null;
    return {
      subfactor_id: sport.id,
      subfactor_name: sport.name,
      primary_id: sport.primaryId,
      primary_name: sport.primaryName,
      parent_option_id: sport.parentOptionId,
      parent_option_name: sport.parentOptionName,
    };
  };


  const saveDraft = () => {
    setLoading(true);
    setError(null);

    const validationErrors = validateFormData(["emailSubject", "emailBody", "emailBodyFr", "organizationName", "organizationNameFr"]);
    if (Object.keys(validationErrors).length > 0) {
      setError(validationErrors);
      setLoading(false);
      return;
    }

    const { selectedSport, ...formDataWithoutSport } = formData;
    const {
      assignedUsersList,
      assignedUsersDraft,
      emailBody,
      emailSubject,
      emailBodyFr,
      collaborators,
      ...restOfFormData
    } = formDataWithoutSport;

    const sportSelectionPayload = mapSportSelection(selectedSport);

    // Common objects
    const emailDraft = {
      task_type: "email_draft",
      subject: restOfFormData.emailSubject || emailSubject,
      body: restOfFormData.emailBody || emailBody,
      body_fr: restOfFormData.emailBodyFr || emailBodyFr,
    };

    // Concatenate the assignedUsersList and assignedUsersDraft removing duplicates
    const updateUserList = assignedUsersList;
    const existingUserList = assignedUsersDraft;

    // If the updateUserList is not empty use it, otherwise use the existingUserList
    const assigneesList = updateUserList.length > 0 ? updateUserList : existingUserList;

    const assigneeDraft = {
      task_type: "assignee_draft",
      assignees: assigneesList,
    };

    const collaboratorsDraft = {
      task_type: "collaborators",
      collaborators: collaborators.map(user => user.value),
    };

    const organizationNameEnTask = {
      task_type: "organization_name_en",
      text: restOfFormData?.organizationName,
    };

    const organizationNameFrTask = {
      task_type: "organization_name_fr",
      text: restOfFormData?.organizationNameFr,
    };

    const surveyIsAnonymous = restOfFormData.user_tasks.find(
      (task) => task.task_type === "survey_is_anonymous"
    );

    if (mode === "edit") {
      // On edit, we must ensure the updated user_tasks array includes the assignee_draft
      const updatedUserTasks = assigneeDraft;

      // Update the email user_tasks with emailDraft
      const updatedEmailTasks = emailDraft;

      // Update the questions user_tasks with the questions
      const updatedQuestionsTasks = restOfFormData.questions.map(q => ({
        ...q,
        task_type: "question",
        options: q.options || [], // Preserve options array
        type: q.type?.value || q.type, // Handle both object and string type formats
      }));
      
      // Update the description user_tasks with the description
      const updatedDescriptionTasks = { text: restOfFormData?.description, task_type: "description" }

      // Update the title user_tasks with the title
      const updatedTitleTasks = { text: restOfFormData?.name, task_type: "name" }

      const updatedOrganizationNameEnTasks = organizationNameEnTask;
      const updatedOrganizationNameFrTasks = organizationNameFrTask;

      // update the survey_is_anonymous user_tasks with the survey_is_anonymous
      const updatedSurveyIsAnonymousTasks = { value: surveyIsAnonymous?.value, task_type: "survey_is_anonymous" }

      // Add selected sport task if sport is selected
      const selectedSportTask = sportSelectionPayload ? {
        task_type: "selected_sport",
        value: sportSelectionPayload,
      } : null;

      // Empty the assignedUsersDraft array
      restOfFormData.assignedUsersDraft = [];

      // Remove user_tasks from restOfFormData
      delete restOfFormData.user_tasks;

      // Get the original created_by task
      const originalCreatedBy = fetchedTemplate?.user_tasks?.find(
        (task) => task.task_type === "created_by"
      );

      // Update the user_tasks array with the survey template sort_order
      const payloadData = {
        ...restOfFormData,
        id: surveyId,
        user_tasks: [
          updatedUserTasks,
          updatedEmailTasks,
          ...updatedQuestionsTasks,
          updatedDescriptionTasks,
          updatedTitleTasks,
          updatedOrganizationNameEnTasks,
          updatedOrganizationNameFrTasks,
          updatedSurveyIsAnonymousTasks,
          collaboratorsDraft,
          ...(selectedSportTask ? [selectedSportTask] : []),
          originalCreatedBy || {
            user_id: Number(get_auth?.employee_id),
            name: `${get_auth?.employee?.first_name} ${get_auth?.employee?.last_name}`,
            account_type: get_auth?.employee?.account_type,
            email: get_auth?.employee?.email,
            organization_id: get_auth?.employee?.organization_id,
            task_type: "created_by",
          },
        ],
      };

      dispatch({
        type: SG_UPDATE_TASK_TEMPLATE,
        payload: payloadData,
      });
    } else {
      // Create scenario: Build "created_by"
      const userObj = {
        user_id: Number(get_auth?.employee_id),
        name: `${get_auth?.employee?.first_name} ${get_auth?.employee?.last_name}`,
        account_type: get_auth?.employee?.account_type,
        email: get_auth?.employee?.email,
        organization_id: get_auth?.employee?.organization_id,
        task_type: "created_by",
      };

      const baseUserTasks =
        restOfFormData?.user_tasks?.length > 0 ? restOfFormData.user_tasks : [];

      // Add selected sport task if sport is selected
      const selectedSportTask = sportSelectionPayload ? {
        task_type: "selected_sport",
        value: sportSelectionPayload,
      } : null;

      const payloadData = {
        ...restOfFormData, // assignedUsersList is no longer included
        user_tasks: [
          ...baseUserTasks,
          userObj,
          emailDraft,
          assigneeDraft,
          collaboratorsDraft,
          surveyIsAnonymous,
          organizationNameEnTask,
          organizationNameFrTask,
          ...restOfFormData?.questions.map((q) => ({
            ...q,
            task_type: "question",
            id: uuidv4(),
            options: q.options || [], // Preserve options array
            type: q.type?.value || q.type, // Handle both object and string type formats
          })),
          { text: restOfFormData?.description, task_type: "description" },
          ...(selectedSportTask ? [selectedSportTask] : []),
        ],
      };

      dispatch({
        type: SG_ADD_TASK_TEMPLATE,
        payload: payloadData,
      });
    }

    addToast("Draft saved successfully", { appearance: "success" });
    navigate(`/app/otp/AI/pulse-survey`);
    setLoading(false);

    dispatch({ type: CLEAR_TASK_TEMPLATE });
  };

  // Handle submit function
  const handleSubmit = () => {
    setLoading(true);
    setError(null);

    const validationErrors = validateFormData();
    if (Object.keys(validationErrors).length > 0) {
      setError(validationErrors);
      setLoading(false);
      return;
    }

    const { selectedSport, ...formDataWithoutSport } = formData;
    const {
      assignedUsersList,
      assignedUsersDraft,
      emailBody,
      emailSubject,
      emailBodyFr,
      collaborators,
      ...restOfFormData
    } = formDataWithoutSport;

    const sportSelectionPayload = mapSportSelection(selectedSport);

    // Common objects
    const emailDraft = {
      task_type: "email_draft",
      subject: restOfFormData.emailSubject || emailSubject,
      body: restOfFormData.emailBody || emailBody,
      body_fr: restOfFormData.emailBodyFr || emailBodyFr,
    };

    // Concatenate the assignedUsersList and assignedUsersDraft removing duplicates
    const updateUserList = assignedUsersList;
    const existingUserList = assignedUsersDraft;

    // If the updateUserList is not empty use it, otherwise use the existingUserList
    const assigneesList = updateUserList.length > 0 ? updateUserList : existingUserList;

    const assigneeDraft = {
      task_type: "assignee_draft",
      assignees: assigneesList,
    };

    const collaboratorsDraft = {
      task_type: "collaborators",
      collaborators: collaborators.map(user => user.value),
    };

    const organizationNameEnTask = {
      task_type: "organization_name_en",
      text: restOfFormData?.organizationName,
    };

    const organizationNameFrTask = {
      task_type: "organization_name_fr",
      text: restOfFormData?.organizationNameFr,
    };

    const surveyIsAnonymous = restOfFormData.user_tasks.find(
      (task) => task.task_type === "survey_is_anonymous"
    );

    if (mode === "edit") {
      // On edit, we must ensure the updated user_tasks array includes the assignee_draft
      const updatedUserTasks = assigneeDraft;

      // Update the email user_tasks with emailDraft
      const updatedEmailTasks = emailDraft;

      // Update the questions user_tasks with the questions
      const updatedQuestionsTasks = restOfFormData.questions.map(q => ({
        ...q,
        task_type: "question",
        options: q.options || [], // Preserve options array
        type: q.type?.value || q.type, // Handle both object and string type formats
      }));
      
      // Update the description user_tasks with the description
      const updatedDescriptionTasks = { text: restOfFormData?.description, task_type: "description" }

      // Update the title user_tasks with the title
      const updatedTitleTasks = { text: restOfFormData?.name, task_type: "name" }

      const updatedOrganizationNameEnTasks = organizationNameEnTask;
      const updatedOrganizationNameFrTasks = organizationNameFrTask;

      // update the survey_is_anonymous user_tasks with the survey_is_anonymous
      const updatedSurveyIsAnonymousTasks = { value: surveyIsAnonymous?.value, task_type: "survey_is_anonymous" }

      // Add selected sport task if sport is selected
      const selectedSportTask = sportSelectionPayload ? {
        task_type: "selected_sport",
        value: sportSelectionPayload,
      } : null;

      // Empty the assignedUsersDraft array
      restOfFormData.assignedUsersDraft = [];

      // Remove user_tasks from restOfFormData
      delete restOfFormData.user_tasks;

      const userObj = {
        user_id: Number(get_auth?.employee_id),
        name: `${get_auth?.employee?.first_name} ${get_auth?.employee?.last_name}`,
        account_type: get_auth?.employee?.account_type,
        email: get_auth?.employee?.email,
        organization_id: get_auth?.employee?.organization_id,
        task_type: "created_by",
      };

      // Update the user_tasks array with the survey template sort_order
      const payloadData = {
        ...restOfFormData,
        id: surveyId,
        triggerSend: true,
        user_tasks: [
          updatedUserTasks,
          updatedEmailTasks,
          ...updatedQuestionsTasks,
          updatedDescriptionTasks,
          updatedTitleTasks,
          updatedOrganizationNameEnTasks,
          updatedOrganizationNameFrTasks,
          updatedSurveyIsAnonymousTasks,
          collaboratorsDraft,
          ...(selectedSportTask ? [selectedSportTask] : []),
          userObj,
        ],
      };

      dispatch({
        type: SG_UPDATE_TASK_TEMPLATE,
        payload: payloadData,
      });
    } else {
      // Build the user object
      const userObj = {
        user_id: Number(get_auth?.employee_id),
        name: `${get_auth?.employee?.first_name} ${get_auth?.employee?.last_name}`,
        account_type: get_auth?.employee?.account_type,
        email: get_auth?.employee?.email,
        organization_id: get_auth?.employee?.organization_id,
        task_type: "created_by",
      };
      setLoadingMessage("Creating survey template...");

      // Add selected sport task if sport is selected
      const selectedSportTask = sportSelectionPayload ? {
        task_type: "selected_sport",
        value: sportSelectionPayload,
      } : null;

      // Prepare payload for survey creation
      const payloadData = {
        ...formDataWithoutSport,
        user_tasks:
          formDataWithoutSport.user_tasks?.length > 0
            ? [
                ...formDataWithoutSport.user_tasks,
                userObj,
                organizationNameEnTask,
                organizationNameFrTask,
                ...formDataWithoutSport?.questions.map((q) => ({
                  ...q,
                  task_type: "question",
                  id: uuidv4(),
                  options: q.options || [], // Preserve options array
                  type: q.type?.value || q.type, // Handle both object and string type formats
                })),
                {
                  text: formDataWithoutSport?.description,
                  task_type: "description",
                  options: [], // Preserve options array
                  type: "text", // Handle string type format
              },
              ...(selectedSportTask ? [selectedSportTask] : []),
              ]
            : [
                userObj,
                organizationNameEnTask,
                organizationNameFrTask,
                ...formDataWithoutSport?.questions.map((q) => ({
                  ...q,
                  task_type: "question",
                  id: uuidv4(),
                  options: q.options || [], // Preserve options array
                  type: q.type?.value || q.type, // Handle both object and string type formats
                })),
                {
                  text: formDataWithoutSport?.description,
                  task_type: "description",
                  options: [], // Preserve options array
                  type: "text", // Handle string type format
              },
              ...(selectedSportTask ? [selectedSportTask] : []),
              ],
      };

      // Add a timeout to simulate clean loading state
      // Dispatch action to create the survey/template
      setTimeout(() => {
        dispatch({
          type: SG_ADD_TASK_TEMPLATE,
          payload: payloadData,
        });
      }, 500);
    }
  };

  // Effect to watch for survey creation
  useEffect(() => {
    if ((createdTemplate?.id && mode !== "edit") || draftSend?.id) {
      // Survey created, now schedule it
      const sportSelectionPayload = mapSportSelection(formData?.selectedSport);

      const payloadObj = {
        task_template_user_tasks: createdTemplate?.id || draftSend?.id,
        name: formData.name,
        assigned_employees: [],
      };

      setLoadingMessage("Sending survey to assignees list...");

      setTimeout(() => {
        dispatch({
          type: SG_CREATE_TASK_SCHEDULE,
          payload: payloadObj,
        });
      }, 500);
    }
  }, [createdTemplate, mode, draftSend, formData.selectedSport]);

  // Effect to watch for scheduling completion
  useEffect(() => {
    if (
      (createdScheduledTemplate?.id && mode !== "edit") ||
      (createdScheduledTemplate?.id && draftSend?.id)
    ) {
      // Create or update the email payload
      const emailPayload = {
        email_subject: formData.emailSubject,
        email_content: formData.emailBody,
        email_content_fr: formData.emailBodyFr,
        task_template: createdScheduledTemplate.id,
      };

      setLoadingMessage("Creating email template...");

      setTimeout(() => {
        dispatch({
          type: SG_ADD_EMAIL_TEMPLATE,
          payload: emailPayload,
        });
      }, 250);
    }
  }, [createdScheduledTemplate, mode]);

  // Effect to watch for email template creation
  useEffect(() => {
    if (
      (createdEmailTemplate?.id && mode !== "edit") ||
      (createdEmailTemplate?.id && draftSend?.id)
    ) {
      const listOne =
        formData?.assignedUsersList?.map((user) => user.key) || [];
      const draftList =
        formData?.assignedUsersDraft?.map((user) => user.key) || [];

      let userList = []
      if (listOne?.length > 0) {
        userList = listOne;
      } else {
        userList = draftList;
      }

      // Remove any duplicates from the finalList
      const uniqueList = [...new Set(userList)];

      // Assign users to the scheduled task
      const userTaskPayload = {
        task_id: createdScheduledTemplate?.id,
        employee_id_list: uniqueList,
        id: createdTemplate?.id || draftSend?.id || fetchedTemplate?.id,
        task_template_user_tasks: createdTemplate?.id || draftSend?.id || fetchedTemplate?.id
      };

      setLoadingMessage("Assigning users to the survey...");

      dispatch({
        type: SG_ADD_USER_TASK,
        payload: userTaskPayload,
      });
    }
  }, [createdEmailTemplate, mode]);


  // Effect to watch for user task assignment
  useEffect(() => {
    if (
      assignedUserTasks.includes(fetchedTemplate?.id) ||
      assignedUserTasks?.includes(createdTemplate?.id) ||
      assignedUserTasks?.includes(draftSend?.id) ||
      assignedUserTasks?.includes(createdScheduledTemplate?.id)
    ) {
      // First update the loading state
      setLoading(false);

      // Add a small delay to ensure state updates are reflected
      setTimeout(() => {
        // Clear navigation state
        dispatch({ type: CLEAR_TASK_TEMPLATE });
        // Show success message
        addToast("Survey created successfully", { appearance: "success" });
        // Navigate after state updates
        navigate(`/app/otp/AI/pulse-survey`);
      }, 100);
    }
  }, [assignedUserTasks]);

  // Effect to watch for survey update
  useEffect(() => {
    if (updatedTemplate?.id && mode === "edit" && !draftSend?.id) {
      setLoading(false);
      navigate(`/app/otp/AI/pulse-survey`);
      addToast("Survey updated successfully", { appearance: "success" });
    }
  }, [updatedTemplate, mode]);

  return (
    <SurveyContext.Provider
      value={{
        formData,
        updateFormData,
        handleSubmit,
        saveDraft,
        loading,
        loadingMessage,
        error,
        isDraft,
      }}
    >
      {children}
    </SurveyContext.Provider>
  );
};
